import { d3, cropr, gettext } from "../adapter";
import each from "lodash/each";
import clone from "lodash/clone";
import extend from "lodash/extend";
import timeFormat from "./chartTimeFormat";
import sensorActions from "../actions/sensorActions";
import moment from "moment";

// For the tooltip
var bisectDate = d3.bisector(function (d) {
  return d.timestamp;
}).left;

// The constructor
class Chart {
  constructor(options, data, dispatch) {
    // Empty data, this is our data format
    this.data = { values: [] };
    this.dispatch = dispatch;
    // Default options
    this.options = {
      id: false,
      class: false,
      width: 500,
      height: 350,
      visible: true,
      showLegend: true,
      depth: false,
      hasTemperature: false,
      editable: false,
      showRaw: false,
      minDraggerDistance: 2, // The minimal distance between the two draggers
      interpolation: "cardinal",
      tension: 0.9,

      // @TODO: Use this to implement units in chart
      i18n: {
        unit: cropr.l10n.unit_precipitation_display,
        unitBig: "cm",
        unitTemperature: cropr.l10n.unit_temp_display, //'°C',
      },
    };

    // Merge the options
    this.options = extend(this.options, options);

    // If a graph is editable, don't show the temperature
    if (this.options.editable) {
      this.options.hasTemperature = false;
    }

    // Set chart dimension, also depending on settings as brush
    // margin top 12 because of the dragger
    var margin = { top: 12, right: 25, bottom: 30, left: 70 };
    var height = this.options.height - (margin.top + margin.bottom);
    var width = this.options.width - (margin.left + margin.right);

    if (this.options.hasTemperature) {
      var betweenChartMargin = 25;

      var temperatureMargin = clone(margin);
      this.temperatureChart = {
        width: width,
        height: height * 0.2,
        margin: temperatureMargin,
      };

      margin.top = margin.top + height * 0.2 + betweenChartMargin;
      this.chart = {
        width: width,
        height: height * 0.8 - betweenChartMargin,
        margin: margin,
      };

      this.totalChart = {
        // The total height of both charts
        height: this.chart.height + this.chart.margin.top - this.temperatureChart.margin.top,
        margin: {
          top: this.temperatureChart.margin.top,
        },
      };
    } else {
      this.chart = {
        width: width,
        height: height,
        margin: margin,
      };

      this.totalChart = {
        height: this.chart.height,
        margin: {
          top: this.chart.margin.top,
        },
      };
    }

    // Set the data
    if (data) {
      this.setData(data);
    }

    // No SVG and elements yet
    this.elements = {};

    var _this = this;
    var scrolling = false;
    this.zoom = d3.behavior
      .zoom()
      .x(this.scales.x)
      .on("zoom", function () {
        var x;
        if (_this.scales.x.domain()[0] < _this.data.minDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.minDate) +
            _this.scales.x.range()[0];
          _this.zoom.translate([x, 0]);
        } else if (_this.scales.x.domain()[1] > _this.data.maxDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.maxDate) +
            _this.scales.x.range()[1];
          _this.zoom.translate([x, 0]);
        }

        // Execute only once
        if (!scrolling) {
          _this.elements.eventRect.classed("grabbing", true);
          dispatch(sensorActions.hovered(false));
        }

        scrolling = true;
        _this._scroll();
      })
      .on("zoomend", function () {
        if (!scrolling) return;

        _this.elements.eventRect.classed("grabbing", false);

        // Enable the pin hovers again when we stop zooming
        if (this === d3.event.sourceEvent.target) {
          dispatch(sensorActions.hovered(true));
        }

        scrolling = false;
        dispatch(
          sensorActions.brushDomain([_this.scales.x.domain()[0], _this.scales.x.domain()[1]])
        );
      });
  }
}

Chart.prototype.initTemperature = function (el) {
  var temperatureChart = (this.elements.temperatureChart = this.elements.svg
    .append("g")
    .attr("class", "temperature chart")
    .attr(
      "transform",
      "translate(" +
        this.temperatureChart.margin.left +
        "," +
        this.temperatureChart.margin.top +
        ")"
    ));

  this.elements.temperatureInnerChart = this.elements.svg
    .append("g")
    .attr("class", "temperature inner")

    .attr("clip-path", "url(#clip-temperature-" + this.options.id + ")")
    .attr(
      "transform",
      "translate(" +
        this.temperatureChart.margin.left +
        "," +
        this.temperatureChart.margin.top +
        ")"
    );

  this.elements.temperatureVisuals = this.elements.temperatureInnerChart
    .append("g")

    .attr("mask", "url(#mask-" + this.options.id + ")")
    .attr("class", "temperature visuals");

  this.elements.temperatureGrid = {
    x: temperatureChart
      .append("g")
      .attr("class", "temperature grid x")
      .attr("transform", "translate(0," + this.temperatureChart.height + ")"),
    y: temperatureChart.append("g").attr("class", "temperature grid y"),
  };

  // Draw the axis
  this.elements.temperatureAxis = {
    x: temperatureChart
      .append("g")
      .attr("class", "temperature axis x")
      .attr("transform", "translate(0," + this.temperatureChart.height + ")"),
    y: temperatureChart.append("g").attr("class", "temperature axis y"),
  };

  this.elements.temperatureLine = this.elements.temperatureInnerChart
    .append("path")
    .attr("class", "line temperature");
};

// Init graph
Chart.prototype.init = function (el) {
  var _this = this;

  // Only init once and only init if we have data
  if (this.svg || !this.data) {
    return false;
  }

  // Create the SVG
  this.elements.svg = d3
    .select(el)
    .append("svg")
    .attr("class", this.options.class)
    .attr("id", this.options.id)
    .attr("width", this.options.width)
    .attr("height", this.options.height);

  if (this.options.hasTemperature) {
    this.initTemperature(el);
  }

  var chart = (this.elements.chart = this.elements.svg
    .append("g")
    .attr("class", "chart")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")"));

  this.elements.innerChart = this.elements.svg
    .append("g")
    .attr("class", "inner")
    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  this.elements.grid = {
    x: chart
      .append("g")
      .attr("class", "grid x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "grid y"),
  };

  // Draw the horizontal lines on the background if we're not editing
  if (!this.options.editable) {
    _drawWarningLines(this);
  }

  this.elements.refillArea = _this.elements.innerChart.append("rect").attr("class", "area refill");

  this.elements.overCapacityFillArea = this.elements.innerChart
    .append("path")
    .attr("clip-path", "url(#overCapacityClip-" + this.options.id + ")")
    .attr("class", "area over-capacity-fill");

  this.elements.refillFillArea = this.elements.innerChart
    .append("path")
    .attr("class", "area refill-fill")
    .attr("clip-path", "url(#refillClip-" + this.options.id + ")");

  // Draw the depth water line
  this.elements.line = this.elements.innerChart.append("path").attr("class", "line water");

  this.elements.chartCover = this.elements.svg
    .append("g")
    .attr("class", "cover")

    // .attr('clip-path', 'url(#clip-' + this.options.id + ')')
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    );

  // Draw the mouse vertical line
  var clipId = "clip";
  if (this.options.hasTemperature) {
    clipId = "clip-total";
  }

  // Draw the today vertical line
  // var today = Date();
  //  this.elements.today = false;
  //  if (this.data.lastDataPoint.timestamp <= today) {
  this.elements.today = this.elements.chartCover
    .append("g")
    .attr("class", "today")
    .attr(
      "transform",
      "translate(" +
        (this.scales.x(new Date(_this.data.lastDataPoint.timestamp)) - 2) +
        "," +
        0 +
        ")"
    );
  //      .attr('transform', 'translate(' + (this.scales.x(this.data.lastDataPoint.timestamp) - 2) + ',' + 0 + ')');

  // 8 is the height of the line element to connect to the circle
  this.elements.today
    .append("line")
    .attr("class", "line today")
    .attr("x1", 2)
    .attr("y1", 8)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.today
    .append("circle")
    .attr("class", "circle today")
    .attr("cx", 2)
    .attr("cy", 1)
    .attr("r", 6);
  // }

  this.elements.mouse = this.elements.chartCover
    .append("g")
    .attr("clip-path", "url(#" + clipId + "-" + this.options.id + ")")
    .append("g")
    .attr("class", "mouse")
    .style("opacity", "0");

  this.elements.mouse
    .append("line")
    .attr("class", "line mouse")
    .attr("x1", 2)
    .attr("y1", 8)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.mouse
    .append("circle")
    .attr("class", "circle mouse")
    .attr("cx", 2)
    .attr("cy", 0)
    .attr("r", 6);

  this.elements.tooltip = this.elements.mouse.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipBalloon = this.elements.tooltip.insert("rect").attr("rx", 2).attr("ry", 2);

  this.elements.tooltip1 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText1 = this.elements.tooltip1.append("text").attr("class", "tooltipText");

  this.elements.tooltip2 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText2 = this.elements.tooltip2.append("text").attr("class", "tooltipText");

  // Draw the axis
  this.elements.axis = {
    x: chart
      .append("g")
      .attr("class", "axis x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "axis y"),
  };

  // Water
  var className = "l-stacked legend has-left-spacing";
  var visibilityText = "";
  if (!this.options.visible) {
    className = "l-stacked legend has-left-spacing is-opacity-half";
    visibilityText = " " + gettext("(not visible in cumulative graph)");
  }

  if (this.options.showLegend) {
    this.elements.legendList = d3.select(el).append("ul").attr("class", className);
    this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend first");
    this.elements.legendLine.append("span").attr("class", "water line").html("&nbsp;");
    this.elements.legendLine
      .append("span")
      .attr("class", "content")
      .html(
        gettext("Development soil moisture at {{depth}} {{unit}} depth")
          .replace("{{depth}}", cropr.l10n_utils.depth_to_l10n(this.options.depth))
          .replace("{{unit}}", cropr.l10n.unit_depth_display) + visibilityText
      );

    if (this.options.editable) {
      // Field refill point
      this.elements.legendLine = this.elements.legendList
        .append("li")
        .attr("class", "legend first");
      this.elements.legendLine.append("span").attr("class", "refill-point line").html("&nbsp;");
      this.elements.legendLine
        .append("span")
        .attr("class", "content")
        .html(
          gettext("Refill point at {{depth}} {{unit}} depth")
            .replace("{{depth}}", cropr.l10n_utils.depth_to_l10n(this.options.depth))
            .replace("{{unit}}", cropr.l10n.unit_depth_display) + visibilityText
        );

      // Field capacity
      this.elements.legendLine = this.elements.legendList
        .append("li")
        .attr("class", "legend first");
      this.elements.legendLine.append("span").attr("class", "field-capacity line").html("&nbsp;");
      this.elements.legendLine
        .append("span")
        .attr("class", "content")
        .html(
          gettext("Field capacity at {{depth}} {{unit}} depth")
            .replace("{{depth}}", cropr.l10n_utils.depth_to_l10n(this.options.depth))
            .replace("{{unit}}", cropr.l10n.unit_depth_display) + visibilityText
        );
    }

    if (this.options.hasTemperature) {
      this.elements.legendLine = this.elements.legendList
        .append("li")
        .attr("class", "legend first");
      this.elements.legendLine.append("span").attr("class", "temperature line").html("&nbsp;");
      this.elements.legendLine
        .append("span")
        .attr("class", "content")
        .html(
          gettext("Development temperature at {{depth}} {{unit}} depth")
            .replace("{{depth}}", cropr.l10n_utils.depth_to_l10n(this.options.depth))
            .replace("{{unit}}", cropr.l10n.unit_depth_display)
        );
    }
  }

  // Add mouseover rectangle
  this.elements.eventRect = this.elements.svg
    .append("svg:rect")
    .attr("class", "rect event")
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    )
    .attr("width", this.chart.width)
    .attr("height", this.totalChart.height)
    .attr("fill", "none")
    .attr("pointer-events", "all");

  // Generating the events for setting the pins
  this.elements.eventRect
    .on("mouseout", function () {
      // on mouse out hide line, circles and text
      _this.dispatch(sensorActions.hovered(false));
    })
    .on("mouseover", function () {
      // on mouse in show line, circles and text
      _this.dispatch(sensorActions.hovered(true));
    })
    .on("mousemove", function () {
      // mouse moving over canvas
      var mouseX = d3.mouse(this)[0]; // mouse position in x
      var date = _this.scales.x.invert(mouseX); // date corresponding to mouse x
      _this.dispatch(sensorActions.hoverDate(date));
    })
    .on("click", function () {
      if (d3.event.defaultPrevented) return;
      _this.dispatch(sensorActions.togglePin());
    });

  // Events for dragging the chart
  this.elements.eventRect
    .call(this.zoom)
    .on("wheel.zoom", null)
    .on("MozMousePixelScroll.zoom", null)
    .on("mousewheel.zoom", null)
    .on("dblclick.zoom", null);

  // Draw the clippaths
  this._drawClipPaths();

  // Draw the horizontal lines on the foreground if we're not editing
  if (this.options.editable) {
    _drawWarningLines(this);
    this.editable();
  }

  this.update();

  function _drawWarningLines(_this) {
    if (_this.options.editable) {
      _this.elements.refillPointLineGroup = _this.elements.svg.append("g");
      _this.elements.refillPointLine = _this.elements.refillPointLineGroup
        .append("line")
        .attr(
          "transform",
          "translate(" + _this.chart.margin.left + "," + _this.totalChart.margin.top + ")"
        )
        .attr("class", "line refill-point");

      // RP tick line and label
      _this.elements.refillPointLineGroup
        .append("line")
        .attr(
          "transform",
          "translate(" + _this.chart.margin.left + "," + _this.totalChart.margin.top + ")"
        )
        .attr("x1", 0)
        .attr("y1", 0)
        .attr("x2", -6)
        .attr("y2", 0)
        .attr("class", "line refill-point");

      _this.elements.refillPointText = _this.elements.refillPointLineGroup
        .append("text")
        .attr("x", _this.chart.margin.left - 9)
        .attr("dy", ".32em")
        .attr("y", 10)
        .attr("text-anchor", "end")
        .text("0 " + cropr.l10n.unit_precipitation_display);

      _this.elements.refillPointRawText = _this.elements.refillPointLineGroup
        .append("text")
        .attr("x", _this.chart.margin.left + _this.chart.width + 6)
        .attr("dy", ".32em")
        .attr("y", 10)
        .text("0 ")
        .style("font-size", "0.75em")
        .on("click", function (d, i) {
          _this.options.showRaw = !_this.options.showRaw;
          _this.update();
        });

      _this.elements.fieldCapacityLineGroup = _this.elements.svg.append("g");
      _this.elements.fieldCapacityLine = _this.elements.fieldCapacityLineGroup
        .append("line")
        .attr(
          "transform",
          "translate(" + _this.chart.margin.left + "," + _this.totalChart.margin.top + ")"
        )
        .attr("class", "line field-capacity");

      // FC tick line
      _this.elements.fieldCapacityLineGroup
        .append("line")
        .attr(
          "transform",
          "translate(" + _this.chart.margin.left + "," + _this.totalChart.margin.top + ")"
        )
        .attr("x1", 0)
        .attr("y1", 0)
        .attr("x2", -6)
        .attr("y2", 0)
        .attr("class", "line field-capacity");

      // FC tick line and label
      _this.elements.fieldCapacityText = _this.elements.fieldCapacityLineGroup
        .append("text")
        .attr("x", _this.chart.margin.left - 9)
        .attr("dy", ".32em")
        .attr("y", 10)
        .attr("text-anchor", "end");
      _this.elements.fieldCapacityRawText = _this.elements.fieldCapacityLineGroup
        .append("text")
        .attr("x", _this.chart.width + _this.chart.margin.left + 6)
        .attr("dy", ".32em")
        .attr("y", 10)
        .style("font-size", "0.75em")
        .on("click", function (d, i) {
          _this.options.showRaw = !_this.options.showRaw;
          _this.update();
        });
    } else {
      _this.elements.refillPointLineGroup = _this.elements.innerChart.append("g");
      _this.elements.refillPointLine = _this.elements.refillPointLineGroup
        .append("line")
        .attr("class", "line refill-point");

      _this.elements.fieldCapacityLineGroup = _this.elements.innerChart.append("g");
      _this.elements.fieldCapacityLine = _this.elements.fieldCapacityLineGroup
        .append("line")
        .attr("class", "line field-capacity");
    }
  }
};

// Set the data we need, we expect to receive it in the correct format
Chart.prototype.setData = function (data) {
  // Set the data
  this.data = extend(this.data, data);
  // Set min and max data value
  // @TODO: define date on before hand in the store
  this.data.minDate = this.data.sensor.measurements.moisture[this.options.depth][0].timestamp;
  this.data.maxDate =
    this.data.sensor.measurements.moisture[this.options.depth][
      this.data.sensor.measurements.moisture[this.options.depth].length - 1
    ].timestamp;

  this.data.refillPoint = this.data.sensor.depths[this.options.depth].relativeRefillPoint;
  this.data.fieldCapacity = this.data.sensor.depths[this.options.depth].relativeFieldCapacity;

  // Create or update scales and axis etc.
  this._setScales();
  this._setAxis();

  this._setDataShapes();
};

Chart.prototype.updateHoverState = function (hovered, pinned, pinDate) {
  if (hovered && !pinned) {
    this.elements.mouse.style("opacity", ".5");
    this.elements.eventRect.classed("grab", false);
  } else if (!pinned) {
    this.elements.mouse.style("opacity", "0");
  } else {
    this.data.pinDate = pinDate;
    this.elements.mouse.style("opacity", "1");
    this.elements.eventRect.classed("grab", true);
  }

  this.data.pinned = pinned;
};

Chart.prototype.updateHoverDate = function (date, pinned) {
  if (!pinned) {
    var xPos = this.scales.x(date);
    this.elements.mouse.attr("transform", "translate(" + (xPos - 2) + "," + 0 + ")");
    var dtool = new Date(date);
    //    this.data.sensor.measurements.temperature[this.options.depth]
    var data = this.data.sensor.measurements.temperature[this.options.depth];
    var datamm = this.data.sensor.measurements.moisture[this.options.depth];
    var i = bisectDate(data, date, 1);
    var d0 = data[i - 1];
    var d1 = data[i];
    var d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;
    var text2 = "";

    if (date) {
      dtool.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
    }

    this.elements.mouse.attr("transform", "translate(" + (this.scales.x(date) - 2) + "," + 0 + ")");
    this.elements.tooltipText1.text(moment(dtool).format("L LT")); // was "%d/%m/%Y %H:%M"
    if (this.options.hasTemperature && typeof d !== "undefined") {
      text2 =
        cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(d.y), 1) +
        " " +
        cropr.l10n.unit_temp_display +
        " / ";
    }

    i = bisectDate(datamm, date, 1);
    d0 = datamm[i - 1];
    d1 = datamm[i];
    d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;

    text2 += cropr.l10n_utils.mm_to_l10n(d.y) + " " + cropr.l10n.unit_precipitation_display;

    this.elements.tooltipText2.text(text2);

    var tooltipMarginLeftRight = 5; // 5px margin
    var tooltipMarginTopBottom = 4; // 5px margin

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltip1.attr("transform", "translate(21, 9)");
      this.elements.tooltipText1.attr("text-anchor", "left");
      this.elements.tooltip2.attr("transform", "translate(21, 24)");
      this.elements.tooltipText2.attr("text-anchor", "left");
    } else {
      this.elements.tooltip1.attr("transform", "translate(-20, 9)");
      this.elements.tooltipText1.attr("text-anchor", "end");
      this.elements.tooltip2.attr("transform", "translate(-20, 24)");
      this.elements.tooltipText2.attr("text-anchor", "end");
    }

    var bbox1 = this.elements.tooltipText1[0][0].getBBox();
    var bbox2 = this.elements.tooltipText2[0][0].getBBox();

    if (bbox1.width > bbox2.width) {
      var bboxWidth = bbox1.width + 2 * tooltipMarginLeftRight;
      var bboxX = bbox1.x;
    } else {
      bboxWidth = bbox2.width + 2 * tooltipMarginLeftRight;
      bboxX = bbox2.x;
    }

    this.elements.tooltipBalloon
      .attr("y", bbox1.y - tooltipMarginTopBottom + 9)
      .attr("width", bboxWidth)
      .attr("height", bbox1.height * 2 + 2 * tooltipMarginTopBottom);

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight + 20);
    } else {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight - 20);
    }
  }
};

// Destroy
Chart.prototype.destroy = function () {
  this.elements.svg.remove();
};

// Update
Chart.prototype.update = function (data) {
  var _this = this;

  if (data) {
    this.setData(data);
    this.zoom.x(this.scales.x);
    this.scales.x.domain(data.domain);
  }

  // Update the line data
  this.elements.line.datum(this.data.sensor.measurements.moisture[this.options.depth]);
  this.elements.overCapacityFillArea.datum(
    this.data.sensor.measurements.moisture[this.options.depth]
  );
  this.elements.refillFillArea.datum(this.data.sensor.measurements.moisture[this.options.depth]);

  // Update the axis
  this.elements.axis.x.transition().call(this.axis.x);
  this.elements.axis.y.transition().call(this.axis.y);
  this.elements.grid.x.transition().call(this.axis.gridX);
  this.elements.grid.y.transition().call(this.axis.gridY);

  this.elements.line.transition().attr("d", this.shapes.line);

  if (this.elements.today) {
    //    this.elements.today.transition().attr('transform', 'translate(' + (this.scales.x(this.data.lastDataPoint.timestamp) - 2) + ',' + 0 + ')');
    this.elements.today
      .transition()
      .attr(
        "transform",
        "translate(" +
          (this.scales.x(new Date(_this.data.lastDataPoint.timestamp)) - 2) +
          "," +
          0 +
          ")"
      );
  }

  if (this.data.pinned) {
    this.elements.mouse
      .transition()
      .attr("transform", "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")");
  }

  // Only show these lines if we have the information
  if (this.data.sensor.fieldCapacity !== null) {
    var fieldCapacity = this.data.fieldCapacity;

    this.elements.fieldCapacityLineGroup
      .transition()
      .attr("transform", "translate(" + 0 + "," + this.scales.y(fieldCapacity) + ")");
    this.elements.fieldCapacityLine
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", this.chart.width)
      .attr("y2", 0);

    // @TODO: when to do transitions and when not
    //this.elements.overCapacityFillArea.transition().attr('d', this.shapes.overCapacityFillArea);
    this.elements.overCapacityFillArea.transition().attr("d", this.shapes.overCapacityFillArea);

    this.defs.overCapacityClip
      .transition()
      .attr("width", this.chart.width)
      .attr("x", 0)
      .attr("y", 0)
      .attr("height", this.scales.y(fieldCapacity));
  }

  if (this.data.refillPoint !== null) {
    var refillPoint = this.data.refillPoint;

    this.elements.refillPointLineGroup
      .transition()
      .attr("transform", "translate(" + 0 + "," + this.scales.y(refillPoint) + ")");
    this.elements.refillPointLine
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", this.chart.width)
      .attr("y2", 0);

    this.elements.refillArea
      .transition()
      .attr("width", this.chart.width)
      .attr("x", 0)
      .attr("y", this.scales.y(refillPoint))
      .attr("height", this.chart.height - this.scales.y(refillPoint));

    this.elements.refillFillArea.transition().attr("d", this.shapes.refillFillArea);

    this.defs.refillClip
      .transition()
      .attr("width", this.chart.width)
      .attr("x", 0)
      .attr("y", this.scales.y(refillPoint))
      .attr("height", this.chart.height - this.scales.y(refillPoint));
  }

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.options.hasTemperature) {
    this.defs.temperatureClip.attr("width", this.chart.width);
    this.elements.temperatureAxis.x.transition().call(this.axis.temperatureX);
    this.elements.temperatureAxis.y.transition().call(this.axis.temperatureY);
    this.elements.temperatureGrid.x.transition().call(this.axis.temperatureGridX);
    this.elements.temperatureGrid.y.transition().call(this.axis.temperatureGridY);

    this.elements.temperatureLine.datum(
      this.data.sensor.measurements.temperature[this.options.depth]
    );
    this.elements.temperatureLine.transition().attr("d", this.shapes.temperatureLine);
  }

  if (this.options.editable) {
    var scale = this.data.sensor.depths[this.options.depth].scale;
    //    var scale = this.data.sensor.depths[this.options.depth].scale;
    var fc = this.data.sensor.depths[this.options.depth].fieldCapacity;
    var rp = this.data.sensor.depths[this.options.depth].refillPoint;
    _this.elements.fieldCapacityRawText.text(Math.round(fc));
    _this.elements.refillPointRawText.text(Math.round(rp));
    if (this.options.showRaw) {
      _this.elements.fieldCapacityRawText.style("fill", "#BBF");
      _this.elements.refillPointRawText.style("fill", "#FBB");
    } else {
      _this.elements.fieldCapacityRawText.style("fill", "#FFF");
      _this.elements.refillPointRawText.style("fill", "#FFF");
    }
    _this.elements.fieldCapacityText.text(
      cropr.l10n_utils.mm_to_l10n(scale) + " " + cropr.l10n.unit_precipitation_display
    );
  }
};

// Not really DRY. The same as update besides the transitions
Chart.prototype._scroll = function () {
  var _this = this;
  this.elements.axis.x.call(this.axis.x);
  this.elements.grid.x.call(this.axis.gridX);
  this.elements.line.attr("d", this.shapes.line);
  if (this.data.fieldCapacity !== null) {
    this.elements.overCapacityFillArea.attr("d", this.shapes.overCapacityFillArea);
  }

  if (this.data.refillPoint !== null) {
    this.elements.refillFillArea.attr("d", this.shapes.refillFillArea);
  }

  if (this.elements.today) {
    //    this.elements.today.attr('transform', 'translate(' + (this.scales.x(this.data.lastDataPoint.timestamp) - 2) + ',' + 0 + ')');
    this.elements.today.attr(
      "transform",
      "translate(" +
        (this.scales.x(new Date(_this.data.lastDataPoint.timestamp)) - 2) +
        "," +
        0 +
        ")"
    );
  }

  if (this.data.pinned) {
    this.elements.mouse.attr(
      "transform",
      "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")"
    );
  }

  if (this.options.hasTemperature) {
    this.elements.temperatureAxis.x.call(this.axis.temperatureX);
    this.elements.temperatureAxis.y.call(this.axis.temperatureY);
    this.elements.temperatureGrid.x.call(this.axis.temperatureGridX);
    this.elements.temperatureGrid.y.call(this.axis.temperatureGridY);

    this.elements.temperatureLine.attr("d", this.shapes.temperatureLine);
  }
};

Chart.prototype.resize = function (width, height) {
  if (width) {
    this.options.width = width;
    this.chart.width = this.options.width - (this.chart.margin.left + this.chart.margin.right);
  }

  if (height) {
    this.options.height = height;
    this.chart.width = this.options.height - (this.chart.margin.top + this.chart.margin.bottom);
  }

  this.elements.svg.style("width", this.options.width);
  this.elements.svg.style("height", this.options.height);

  // Update the scales
  this._setScales();
  this._setAxis();

  this.update();

  // http://eyeseast.github.io/visible-data/2013/08/28/responsive-charts-with-d3/
};

Chart.prototype._setScales = function () {
  var _this = this;

  // We need to find the highest individual to align scales of the sub graphs
  // We can optimize by removing this function - It might be executed up to 12 times
  var allValues = [];
  var domainStart = this.data.domain[0];
  var domainStop = this.data.domain[1];
  each(this.data.sensor.measurements.moisture, function (values, depth) {
    if (_this.options.editable && depth !== _this.options.depth) {
      return;
    }
    // Push the refil points and field capacities
    var fieldCapacity = Number(_this.data.fieldCapacity);
    var refillPoint = Number(_this.data.refillPoint);
    var tolerance = (fieldCapacity - refillPoint) * 0.2;

    allValues.push(fieldCapacity + tolerance);
    allValues.push(refillPoint - tolerance);

    var visibleIndexes = [];

    // Push the actual values
    each(values, function (value, index) {
      // Change the y domain based on the code
      // if (value.active && value.timestamp > domainStart && value.timestamp < domainStop) {
      if (value.timestamp > domainStart && value.timestamp < domainStop) {
        visibleIndexes.push(index);
        allValues.push(Number(value.y));
      }
    });

    if (visibleIndexes[0] > 0) {
      allValues.push(Number(values[visibleIndexes[0] - 1].y));
    }

    if (visibleIndexes[visibleIndexes.length - 1] < values.length - 1) {
      allValues.push(Number(values[visibleIndexes[visibleIndexes.length - 1] + 1].y));
    }

    // If there are no measurements inside the period for the specific depth
    if (visibleIndexes.length === 0) {
      // Find the closest value
      var smallest = _this.data.minDate;
      var smallestKey = 0;
      var largest = _this.data.maxDate;
      var largestKey = values.length - 1;

      each(values, function (value, key) {
        if (value.timestamp > domainStop && value.timestamp < largest) {
          largest = value.timestamp;
          largestKey = key;
        }

        if (value.timestamp < domainStart && value.timestamp > smallest) {
          smallest = value.timestamp;
          smallestKey = key;
        }
      });

      if (values.length > 0) {
        allValues.push(Number(values[largestKey].y));
        allValues.push(Number(values[smallestKey].y));
      }
    }
  });

  // Define domain for temperature
  var allTemperatures = [];
  each(this.data.sensor.measurements.temperature, function (values, depth) {
    // Store the indexes that are visible within the domain
    var visibleIndexes = [];

    // Push the actual values
    each(values, function (value, index) {
      // Change the y domain based on the code
      if (value.timestamp >= domainStart && value.timestamp <= domainStop) {
        visibleIndexes.push(index);
        allTemperatures.push(Number(value.y));
      }
    });

    // Add one datum before to define the scale
    if (visibleIndexes[0] > 0) {
      allTemperatures.push(Number(values[visibleIndexes[0] - 1].y));
    }

    if (visibleIndexes[visibleIndexes.length - 1] < values.length - 1) {
      allTemperatures.push(Number(values[visibleIndexes[visibleIndexes.length - 1] + 1].y));
    }

    // If there are no measurements inside the period for the specific depth
    if (visibleIndexes.length === 0) {
      // Find the closest value
      var smallest = _this.data.minDate;
      var smallestKey = 0;
      var largest = _this.data.maxDate;
      var largestKey = values.length - 1;

      each(values, function (value, key) {
        if (value.timestamp > domainStop && value.timestamp < largest) {
          largest = value.timestamp;
          largestKey = key;
        }

        if (value.timestamp < domainStart && value.timestamp > smallest) {
          smallest = value.timestamp;
          smallestKey = key;
        }
      });

      if (values.length > 0) {
        allTemperatures.push(Number(values[largestKey].y));
        allTemperatures.push(Number(values[smallestKey].y));
      }
    }

    // If all temperatures are 0, add 10 to the list so we have a scale
    if (d3.max(allTemperatures) === 0) {
      allTemperatures.push(10);
    }
  });

  this.scales = {
    x: d3.time.scale().range([0, this.chart.width]).domain(this.data.domain),
    y: d3.scale
      .linear()
      .range([this.chart.height, 0])
      .domain([d3.min(allValues), d3.max(allValues)]),
  };

  if (this.options.hasTemperature) {
    this.scales.temperatureY = d3.scale
      .linear()
      .range([this.temperatureChart.height, 0])
      .domain([d3.min(allTemperatures), d3.max(allTemperatures)]);
  }
};

Chart.prototype._setAxis = function () {
  var _this = this;

  // Calculate the amount of ticks
  var ticksY = 0;
  if (!this.options.editable) {
    ticksY = Math.round(this.chart.height / 40);
  }

  var ticksX = Math.round(this.chart.width / 150);

  this.axis = {
    x: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat(timeFormat.chart),
    y: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return cropr.l10n_utils.mm_to_l10n(d) + " " + _this.options.i18n.unit;
      }),

    gridX: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.chart.height, 0, 0)
      .tickFormat(""),
    gridY: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY * 2)
      .tickSize(-this.chart.width, 0, 0)
      .tickFormat(""),
  };

  if (this.options.hasTemperature) {
    var ticksTemperatureY = Math.round(this.temperatureChart.height / 15);

    this.axis.temperatureX = d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat("");

    this.axis.temperatureY = d3.svg
      .axis()
      .scale(this.scales.temperatureY)
      .orient("left")
      .ticks(ticksTemperatureY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return (
          cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(d), 1) +
          " " +
          _this.options.i18n.unitTemperature
        );
      });

    this.axis.temperatureGridX = d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.temperatureChart.height, 0, 0)
      .tickFormat("");
    this.axis.temperatureGridY = d3.svg
      .axis()
      .scale(this.scales.temperatureY)
      .orient("left")
      .ticks(ticksTemperatureY)
      .tickSize(-this.temperatureChart.width, 0, 0)
      .tickFormat("");
  }
};

Chart.prototype._setDataShapes = function () {
  var _this = this;
  this.shapes = {
    line: d3.svg
      .line()

      // .interpolate(this.options.interpolation)
      // .tension(this.options.tension)

      // Does the line need to be drawed?
      // .defined(function (d) {
      //   return d.active === true;
      // })

      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        return _this.scales.y(d.y);
      }),

    overCapacityFillArea: d3.svg
      .area()

      //.interpolate(this.options.interpolation)
      //.tension(this.options.tension)
      // .defined(function(d) { return d.active === true; })
      .x(function (d) {
        return _this.scales.x(d.timestamp) || 1;
      })
      .y0(function (d) {
        return _this.scales.y(d.y);
      })
      .y1(function (d) {
        return _this.scales.y(_this.data.fieldCapacity);
      }),

    // The fill of the graph when it goes below refill point
    refillFillArea: d3.svg
      .area()

      //.interpolate(this.options.interpolation)
      //.tension(this.options.tension)
      // .defined(function(d) { return d.active === true; })
      .x(function (d) {
        return _this.scales.x(d.timestamp) || 1;
      })
      .y0(function (d) {
        return _this.scales.y(d.y);
      })
      .y1(function (d) {
        return _this.scales.y(_this.data.refillPoint);
      }),
  };

  if (this.options.hasTemperature) {
    this.shapes.temperatureLine = d3.svg
      .line()

      //.interpolate(this.options.interpolation)
      //.tension(this.options.tension)
      // .defined(function(d) { return d.active === true; })
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        return _this.scales.temperatureY(d.y);
      });
  }
};

Chart.prototype._drawClipPaths = function () {
  var defs;
  this.defs = defs = this.elements.svg.append("defs");
  this.defs.clip = defs
    .append("svg:clipPath")
    .attr("id", "clip-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-8")
    .attr("height", this.chart.height + 8);

  this.defs.totalClip = defs
    .append("svg:clipPath")
    .attr("id", "clip-total-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-8")
    .attr("height", this.totalChart.height + 8);

  if (this.options.hasTemperature) {
    this.defs.temperatureClip = defs
      .append("svg:clipPath")
      .attr("id", "clip-temperature-" + this.options.id)
      .append("svg:rect")
      .attr("x", "0")
      .attr("y", "-8")
      .attr("height", this.temperatureChart.height + 8);
  }

  this.defs.overCapacityClip = defs
    .append("clipPath")
    .attr("id", "overCapacityClip-" + this.options.id)
    .append("rect");
  this.defs.refillClip = defs
    .append("clipPath")
    .attr("id", "refillClip-" + this.options.id)
    .append("rect");
};

Chart.prototype.editable = function (data) {
  var _this = this;
  var draggerSize = 24; // The width and height of the dragger

  // Field capacity dragger
  var fieldCapacityDragger = this.elements.fieldCapacityLineGroup
    .append("g")
    .attr("class", "draggerlines blue")
    .attr(
      "transform",
      "translate(" +
        (this.chart.width / 2 + 5 - draggerSize / 2 + this.chart.margin.left) +
        ", -" +
        (draggerSize / 2 - this.totalChart.margin.top) +
        ")"
    );

  fieldCapacityDragger
    .append("rect")

    .attr("rx", 2)
    .attr("ry", 2)
    .attr("width", draggerSize + 1)
    .attr("height", draggerSize - 4)
    .attr("class", "dragger blue");

  fieldCapacityDragger
    .append("rect")
    .attr("y", 5)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", draggerSize - 13)
    .attr("height", 2);
  fieldCapacityDragger
    .append("rect")
    .attr("y", 9)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", draggerSize - 13)
    .attr("height", 2);
  fieldCapacityDragger
    .append("rect")
    .attr("y", 13)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", draggerSize - 13)
    .attr("height", 2);

  // Refillpiont dragger
  var refillPointDragger = this.elements.refillPointLineGroup
    .append("g")
    .attr("class", "draggerlines red")
    .attr(
      "transform",
      "translate(" +
        (this.chart.width / 2 - 5 - draggerSize / 2 + this.chart.margin.left) +
        ", -" +
        (draggerSize / 2 - this.totalChart.margin.top) +
        ")"
    );

  refillPointDragger
    .append("rect")

    .attr("rx", 2)
    .attr("ry", 2)
    .attr("width", draggerSize + 1)
    .attr("height", draggerSize - 4)
    .attr("class", "dragger red");

  refillPointDragger
    .append("rect")
    .attr("y", 5)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", draggerSize - 13)
    .attr("height", 2);
  refillPointDragger
    .append("rect")
    .attr("y", 9)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", draggerSize - 13)
    .attr("height", 2);
  refillPointDragger
    .append("rect")
    .attr("y", 13)
    .attr("rx", 1)
    .attr("x", 7)
    .attr("width", draggerSize - 13)
    .attr("height", 2);

  var dragFieldCapacity = d3.behavior
    .drag()
    .on("drag", _onDragFieldCapacity)
    .on("dragend", _onDropFieldCapacity);

  var dragRefillPoint = d3.behavior
    .drag()
    .on("drag", _onDragRefillPoint)
    .on("dragend", _onDropRefillPoint);

  function _onDragFieldCapacity() {
    _onDrag(this, d3.event, "fieldCapacity");
  }

  function _onDragRefillPoint() {
    _onDrag(this, d3.event, "refillPoint");
  }

  function _onDropFieldCapacity() {
    _this.dispatch(
      sensorActions.updateFieldCapacity(_this.options.depth, _this.data.fieldCapacity)
    );
  }

  function _onDropRefillPoint() {
    _this.dispatch(sensorActions.updateRefillPoint(_this.options.depth, _this.data.refillPoint));
  }

  function _onDrag(element, event, lineType) {
    // Knobs responsive
    // Live update van de data + vinkje alles updaten
    // Tooltip die je het aantal mm weergeeft??
    // Fix transities op de vlakken goed regelen
    // Zorgen dat de knob aan de bovenkant niet uit de grafiek loopt

    var line = d3.select(element);
    var t = d3.transform(line.attr("transform"));

    var y = t.translate[1] + event.dy;
    // y moet binnen de grafiek vallen
    y = Math.max(0, Math.min(y, _this.chart.height));

    if (lineType === "refillPoint") {
      y = Math.max(_this.scales.y(_this.data.fieldCapacity) + _this.options.minDraggerDistance, y); // Math.min(y, _this.chart.height));
    } else {
      y = Math.min(_this.scales.y(_this.data.refillPoint) - _this.options.minDraggerDistance, y); //), Math.min(y, _this.chart.height));
    }

    // line.attr('transform', 'translate(' + 0 + ',' + y + ')');

    var lineValue = _this.scales.y.invert(y);

    // Define the type of change
    // Update the value
    _this.data[lineType] = lineValue;

    //_this.data[lineType].depthValues[_this.options.depth] = lineValue;
    if (lineType === "refillPoint") {
      _this.elements.refillPointLineGroup.attr(
        "transform",
        "translate(" + 0 + "," + _this.scales.y(lineValue) + ")"
      );
      _this.elements.refillArea
        .attr("width", _this.chart.width)
        .attr("x", 0)
        .attr("y", _this.scales.y(lineValue))
        .attr("height", _this.chart.height - _this.scales.y(lineValue));
      _this.elements.refillFillArea.attr("d", _this.shapes.refillFillArea);
      _this.defs.refillClip
        .attr("width", _this.chart.width)
        .attr("x", 0)
        .attr("y", _this.scales.y(lineValue))
        .attr("height", _this.chart.height - _this.scales.y(lineValue));
    } else {
      _this.elements.fieldCapacityLineGroup.attr(
        "transform",
        "translate(" + 0 + "," + _this.scales.y(lineValue) + ")"
      );
      _this.elements.overCapacityFillArea.attr("d", _this.shapes.overCapacityFillArea);
      _this.defs.overCapacityClip
        .attr("width", _this.chart.width)
        .attr("x", 0)
        .attr("y", 0)
        .attr("height", _this.scales.y(lineValue));
    }
  }

  this.elements.fieldCapacityLineGroup.call(dragFieldCapacity);
  this.elements.refillPointLineGroup.call(dragRefillPoint);
};

export default Chart;
